import React, { useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Box,
  TextField,
  Typography,
  Snackbar,
  Alert,
  InputLabel,
  FormControl,
  Autocomplete,
  Chip,
  CircularProgress,
  Button as MUIButton,
} from "@mui/material";
import { addBroadcastContent } from "../../../store/api/content";
import Button from "../../common/Button";
import { useOrganisation } from "../../../hooks/useOrganisation";
import { useNavigate } from "react-router-dom";
import { getAllGroups } from "../../../store/api/groups";

// Special option objects for Select All / Unselect All
const SELECT_ALL_OPTION = {
  id: "select-all",
  name: "SELECT ALL",
  isSelectAllOption: true,
};
const UNSELECT_ALL_OPTION = {
  id: "unselect-all",
  name: "UNSELECT ALL",
  isUnselectAllOption: true,
};

const SendCustomEmail = () => {
  const navigate = useNavigate();
  const { companyName, organisation, primaryColor } = useOrganisation();

  const [subject, setSubject] = useState("");
  const [contentHtmlSubject, setContentHtmlsubject] = useState("");
  const [contentTextSubject, setContentTextsubject] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [errors, setErrors] = useState({
    subject: false,
    content: false,
  });
  const [isSuccess, setIsSuccess] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("name");
  const [isDescending, setIsDescending] = useState(false);

  // State for error toast only
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const { data: groupsData, isLoading: isLoadingGroups } = useQuery({
    queryKey: [
      "adminGroups",
      searchTerm,
      currentPage,
      sortBy,
      isDescending,
      organisation,
    ],
    queryFn: () =>
      getAllGroups({
        searchTerm,
        pageNumber: currentPage,
        sortBy,
        isDescending,
        organisationId: organisation?.id,
      }),
  });

  const allOptions = groupsData?.paginatedContent
    ? [SELECT_ALL_OPTION, UNSELECT_ALL_OPTION, ...groupsData.paginatedContent]
    : [SELECT_ALL_OPTION, UNSELECT_ALL_OPTION];

  const { mutate, isLoading } = useMutation({
    mutationFn: addBroadcastContent,
    onSuccess: () => {
      setIsSuccess(true);

      setSubject("");
      setContentHtmlsubject("");
      setSelectedGroups([]);
    },
    onError: (error) => {
      setToast({
        open: true,
        message: "Failed to send broadcast content",
        severity: "error",
      });
      console.error(error);
    },
  });

  const handleSend = () => {
    if (!subject || !contentHtmlSubject) {
      setErrors({
        subject: !subject,
        content: !contentHtmlSubject,
      });
      return;
    }

    // Extract plain text content for `contentTextsubject`
    const plainText = contentHtmlSubject.replace(/<[^>]*>/g, ""); // Remove HTML tags
    setContentTextsubject(plainText);

    const data = {
      subject,
      contentText: plainText,
      contentHtml: contentHtmlSubject,
      groupIds: selectedGroups.map((group) => group.id),
    };

    mutate(data);
  };

  const handleBackToDashboard = () => {
    navigate(`/${companyName}/dashboard`);
  };

  const handleSendAnother = () => {
    setIsSuccess(false);
  };

  const handleCloseToast = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setToast({ ...toast, open: false });
  };

  const handleGroupsChange = (event, values) => {
    if (values.some((item) => item.isSelectAllOption)) {
      const actualGroups = groupsData?.paginatedContent || [];
      setSelectedGroups([...actualGroups]);
      return;
    }

    if (values.some((item) => item.isUnselectAllOption)) {
      setSelectedGroups([]);
      return;
    }

    setSelectedGroups(
      values.filter((val) => !val.isSelectAllOption && !val.isUnselectAllOption)
    );
  };

  // Success screen
  if (isSuccess) {
    return (
      <Box sx={{ maxWidth: "1200px", mx: "auto", p: 3 }}>
        <Typography variant="h5" sx={{ mb: 4, fontWeight: 500 }}>
          Critical content
        </Typography>

        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "300px",
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 4, fontWeight: 500, fontSize: "1.5rem" }}
          >
            Broadcast sent successfully!
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Button
              onClick={handleSendAnother}
              text="Send another"
              backgroundColor="white"
            />

            <Button onClick={handleBackToDashboard} text="Return to Admin" />
          </Box>
        </Box>
      </Box>
    );
  }

  // Form screen
  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", p: 3 }}>
      <Typography variant="h5" sx={{ mb: 4, fontWeight: 500 }}>
        Critical content
      </Typography>

      <Box sx={{ mb: 3 }}>
        <InputLabel htmlFor="subject" sx={{ mb: 1, fontSize: "0.875rem" }}>
          Subject
        </InputLabel>
        <TextField
          id="subject"
          variant="outlined"
          fullWidth
          size="small"
          error={errors.subject}
          helperText={errors.subject ? "Subject is required" : ""}
          value={subject}
          onChange={(e) => {
            setSubject(e.target.value);
            setErrors({ ...errors, subject: false });
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 1,
            },
          }}
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <InputLabel htmlFor="groups" sx={{ mb: 1, fontSize: "0.875rem" }}>
          Groups (Optional)
        </InputLabel>

        <FormControl
          fullWidth
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 1,
            },
          }}
        >
          {isLoadingGroups ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Autocomplete
              multiple
              id="groups"
              options={allOptions}
              getOptionLabel={(option) => {
                if (option.isSelectAllOption || option.isUnselectAllOption) {
                  return "";
                }
                return option.name;
              }}
              value={selectedGroups}
              onChange={handleGroupsChange}
              disableCloseOnSelect={true}
              renderInput={(params) => (
                <TextField {...params} placeholder={""} size="small" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.id}
                    label={option.name}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderOption={(props, option) => {
                if (option.isSelectAllOption || option.isUnselectAllOption) {
                  return (
                    <li
                      {...props}
                      style={{
                        padding: "0",
                        margin: "0",
                      }}
                    >
                      <Button
                        text={option.name}
                        sx={{
                          width: "100%",
                          borderRadius: "0",
                          margin: "0",
                          padding: "0px",
                          justifyContent: "flex-start",
                          textAlign: "left",
                        }}
                      />
                    </li>
                  );
                }
                return <li {...props}>{option.name}</li>;
              }}
            />
          )}
        </FormControl>
      </Box>

      <Box sx={{ mb: 4 }}>
        <InputLabel htmlFor="content" sx={{ mb: 1, fontSize: "0.875rem" }}>
          Content:
        </InputLabel>
        <Box
          sx={{
            border: errors.content ? "1px solid #d32f2f" : "1px solid #e0e0e0",
            borderRadius: 1,
            "& .quill": {
              "& .ql-toolbar": {
                borderTop: "none",
                borderLeft: "none",
                borderRight: "none",
                borderBottom: "1px solid #e0e0e0",
              },
              "& .ql-container": {
                border: "none",
                minHeight: "200px",
              },
            },
          }}
        >
          <ReactQuill
            theme="snow"
            value={contentHtmlSubject}
            onChange={(value) => {
              setContentHtmlsubject(value);
              setErrors({ ...errors, content: false });
            }}
            modules={{
              toolbar: [
                ["bold", "italic", "underline", "link"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["code-block"],
                ["clean"],
              ],
            }}
          />
        </Box>
        {errors.content && (
          <Typography
            color="error"
            variant="caption"
            sx={{ mt: 0.5, display: "block" }}
          >
            Content is required
          </Typography>
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
        <Button
          onClick={handleBackToDashboard}
          text="Back to dashboard"
          backgroundColor="white"
        />

        <Button
          onClick={handleSend}
          disabled={isLoading}
          text={
            isLoading
              ? "Sending..."
              : selectedGroups.length > 0
              ? "Send to selected groups"
              : "Send to all"
          }
        />
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SendCustomEmail;
